import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8011';

const CategoryCards = () => {
  const { project } = useParams(); // Get project name from the URL
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/expenses/getAllExpenses`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch expenses');
        }

        const data = await response.json();
        const filteredExpenses = data.filter(expense => expense.project === project);

        const categoryMap = filteredExpenses.reduce((acc, expense) => {
          if (!acc[expense.category]) {
            acc[expense.category] = 0;
          }
          acc[expense.category] += expense.price;
          return acc;
        }, {});

        const categories = Object.entries(categoryMap).map(([category, total]) => ({
          category,
          total,
        }));
        setCategories(categories);
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }
    };

    fetchExpenses();
  }, [project]);

  const handleDownloadPdf = () => {
    const projectName = decodeURIComponent(project);
    const safeFileName = projectName
      .trim()
      .replace(/[^\w-]+/g, '_')
      .replace(/_+/g, '_');
    const grandTotal = categories.reduce((sum, cat) => sum + cat.total, 0);

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text(`Expense Sheet - ${projectName}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 26);

    const rows = categories.length
      ? categories.map((cat) => [cat.category, `Rs. ${cat.total.toFixed(2)}`])
      : [['No expenses found', 'Rs. 0.00']];

    autoTable(doc, {
      startY: 34,
      head: [['Category', 'Expense']],
      body: rows,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [49, 54, 63],
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    const finalY = doc.lastAutoTable?.finalY ?? 34;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Expense: Rs. ${grandTotal.toFixed(2)}`, 14, finalY + 12);

    doc.save(`${safeFileName || 'expense-sheet'}.pdf`);
  };

  return (
    <div className=''>
      <div className='bg-white p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <button onClick={() => navigate(-1)}> {/* Navigate to the previous page */}
          <i className="fa-solid fa-arrow-left text-xl me-2"></i>
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="bg-themeColor1 text-themeColor2 px-4 py-2 rounded-md font-semibold hover:opacity-90 transition-opacity"
        >
          Download Expense Sheet PDF
        </button>
      </div>
      <h2 className="text-xl  font-semibold bg-themeColor2 p-3 rounded-lg text-themeColor1 m-2">Categories in {decodeURIComponent(project)}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 m-2">
        {categories.map((cat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-4">
              <h3 className="text-xl font-semibold text-themeColor1">{cat.category}</h3>
              <p className="text-themeColor2 font-semibold">Rs. {cat.total.toFixed(2)}</p>
            </div>
            <div className="bg-gray-100 p-4 text-center">
              <Link to={`/project/${encodeURIComponent(project)}/category/${encodeURIComponent(cat.category)}`} className="text-themeColor1 hover:underline hover:text-themeColor2">
                View More <i className="fa fa-arrow-circle-right"></i>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryCards;
